import React, {FC, Fragment, ReactElement, useMemo} from 'react';
import {useQuerySearchParams} from "../../../../config/queryClient";
import {Button, Card, Table, Tag, Typography} from "antd";
import {StudentExerciseAttempt} from "../../../../types";
import {ColumnsType} from "antd/lib/table";
import {useLabels} from "../../../../hooks/Labels";
import {DATATABLE_VERTICAL_SCROLL, GUTTER} from "../../../../constants";
import ReactMarkdown from 'react-markdown';
import { CompetencyLevel, ISectionFeedback, StructuredFeedback } from './types';
import { getCompetencyColor, getCompetencyName, prettifyCompetency } from './utils';
import { Action, Event } from '../ConversationStory/types';
import ReferenceBadge from '../ConversationStory/ReferenceBadge';
import { formatTimePassed } from '../../../../utils';
import { getCreatedAtDelta } from '../ConversationStory/utils';
const { Title, Paragraph } = Typography;



const getColumns = (sections: ISectionFeedback[]): ColumnsType<any> => {
    const columns: ColumnsType<ISectionFeedback> = [
        {
            title: 'Section',
            dataIndex: 'title',
            key: 'title'
        },
    ];
    
    const hasCompetencyLevels = sections.some(section => !!section.feedback?.competency_level);
    if (hasCompetencyLevels) {
        columns.push(
            {
                title: 'Competency',
                dataIndex: ['feedback', 'competency_level'],
                render: (value: CompetencyLevel | undefined, record: ISectionFeedback) => {
                    if (!value) return null;
                    const competencyLevelName = getCompetencyName(value);
                    const color = getCompetencyColor(value);
                    return (
                        <Tag color={color}> 
                            {prettifyCompetency(competencyLevelName)}
                        </Tag>
                    );
                }
            }
        );
    }

    const hasScores = sections.some(section => section.feedback?.score !== undefined && section.feedback.score !== null);
    if (hasScores) {
        columns.push(
            {
                title: 'Score',
                dataIndex: ['feedback', 'score'],
                render: value => value + '%'
            }
        );
    }

    return columns;
}


export const SectionFeedback: FC<{
    feedback: string | StructuredFeedback; 
    userEventsMap: Map<Action<unknown>['id'], Event>
}> = ({feedback, userEventsMap}): ReactElement => {
    if (typeof feedback === 'string') {
        return (
            <div>
                <ReactMarkdown>
                    {feedback}
                </ReactMarkdown>
            </div>
        );
    }
    else {
        return (
            <div>
                <div>
                    <Title level={5}>Summary</Title>
                    <ReactMarkdown>
                        {feedback.summary}
                    </ReactMarkdown>
                </div>
                <div>
                    <Title level={5}>Feedback</Title>
                    <Paragraph>
                    {
                        feedback.feedback.map((feedbackClaim, i) => (
                            <Fragment key={i}>
                                {feedbackClaim.text}
                                {
                                    feedbackClaim.user_action_ids
                                        .map(Number)
                                        .filter(actionId =>
                                            userEventsMap.has(actionId) 
                                            && userEventsMap.get(actionId)?.created_at_delta !== undefined,
                                        )
                                        .map(actionId => (
                                            <Button type="text"
                                                style={{padding: '4px'}}
                                                key={actionId}
                                                onClick={() =>
                                                    document.getElementById(`user-messsage-${actionId}`)?.scrollIntoView()
                                                }
                                            >
                                                <ReferenceBadge>
                                                    {formatTimePassed(
                                                        userEventsMap.get(actionId)!.created_at_delta!,
                                                    )}
                                                </ReferenceBadge>
                                            </Button>
                                        )
                                        )
                                }
                            </Fragment>
                        ))
                    }
                    </Paragraph>
                </div>
                <div>
                    <Title level={5}>Opportunity</Title>
                    <ReactMarkdown>
                        {feedback.opportunity}
                    </ReactMarkdown>
                </div>

            </div>
        )
    }
};


interface AttemptPerformanceDataTableCardProps {
    attemptId: string;
}

export const AttemptPerformanceDataTableCard: FC<AttemptPerformanceDataTableCardProps> = ({attemptId}): ReactElement => {
    const {t} = useLabels();

    const {data, isLoading} = useQuerySearchParams<StudentExerciseAttempt>(
        `/reports/student-exercise-attempts/${attemptId}/`, [], {
            defaultFilters: {
                fields: 'id,feedback'
            }
        })

    const sections = useMemo<ISectionFeedback[]>(() => {
        const feedbackSections: ISectionFeedback[] = data?.feedback?.stages?.at(0)?.marking_feedback?.sections || [];
        return feedbackSections
            .map((section: ISectionFeedback) => {
                const sectionRowData = { ...section };
                if(section.sections) {
                    const subsectionsRowData = section.sections.map(section => ({...section}));
                    if(section.title) {
                        return [sectionRowData, ...subsectionsRowData];
                    }
                    return subsectionsRowData;
                } else {
                    return sectionRowData;
                }
            })
            .flat();
    }, [data]);

    
    const columns = getColumns(sections);

    let title: React.ReactNode = t('section_performance_title');
    const competencyLevel = data?.feedback?.stages?.at(0)?.marking_feedback?.feedback?.competency_level;
    if (competencyLevel) {
        const competencyLevelName = getCompetencyName(competencyLevel);
        const color = getCompetencyColor(competencyLevel);
        title = <div style={{display: 'flex', alignItems: 'center',}}>
            <Title level={3} style={{display: 'inline', marginBottom: '0', marginRight: '2rem'}}>{title}</Title>
            <Tag color={color}>
                {prettifyCompetency(competencyLevelName)}
            </Tag>
        </div>
    }

    const userEventsMap = useMemo<Map<Action<unknown>['id'], Event>>(() => {
        if (!data?.feedback?.stages?.at(0)) return new Map();
        const events = data.feedback.stages?.at(0)?.events as Event[];
        const userEventMap = new Map<
            number,
            Event & { created_at_delta: number }
        >();
        events
            .filter(event => !!event.user_action)
            .forEach(event => {
                userEventMap.set(event.user_action!.id, {
                    ...event,
                    created_at_delta: getCreatedAtDelta(event, events[0]),
                });
            });
        return userEventMap;
    }, [data]);

    return (
        <Card title={title} className='data-table-card'>
            {
                !!data && !isLoading
                && !!data.feedback?.stages?.at(0)?.marking_feedback?.feedback?.generated_feedback
                &&
                    <div style={{padding: `${GUTTER[0]}px`}}>
                        <ReactMarkdown>
                            {data.feedback.stages[0].marking_feedback.feedback.generated_feedback}
                        </ReactMarkdown>
                    </div>
            }
            {
                (isLoading || sections.length > 0)
                && <Table
                    loading={isLoading}
                    dataSource={sections}
                    columns={columns}
                    rowKey='title'
                    pagination={false}
                    rowClassName={record => !!record.sections?.length ? 'heading-row' : ''}
                    expandable={{
                        rowExpandable: (record: ISectionFeedback) => (
                            !!record.feedback?.generated_feedback
                        ),
                        expandedRowRender: (record: ISectionFeedback) => (
                            <SectionFeedback 
                                feedback={record.feedback!.generated_feedback}
                                userEventsMap={userEventsMap}
                            />
                        ),
                    }}
                    scroll={DATATABLE_VERTICAL_SCROLL}
                />
            }
        </Card>
    );
};
